/**
 * Durable retry for question attempts.
 *
 * An attempt POST that fails used to be dropped on the floor: the UI revealed
 * the answer with a hard-coded `xpEarned: 0`, and the student lost the XP, the
 * streak and the progress for a question they had actually answered correctly.
 * That is the bug this exists to prevent.
 *
 * Failures are retried in-process first, then parked in localStorage so they
 * survive a reload, a closed tab, or the app being backgrounded mid-session.
 *
 * Only NETWORK-shaped failures are retried. A 4xx means the server understood
 * the request and rejected it, so replaying it would just fail again (and, for
 * an attempt that actually landed but whose response was lost, would risk
 * double-counting). Retrying only transport failures keeps that risk small.
 */

const KEY = 'squishy_pending_attempts';
const MAX_QUEUED = 200;
const RETRY_DELAYS_MS = [400, 1500];

export interface QueuedAttempt {
  clientAttemptId: string;
  questionId: string;
  correct: boolean;
  firstTry: boolean;
  selectedAnswers?: number[];
  studySessionId?: string | null;
  timeMs?: number;
  queuedAt: number;
}

/** Stable per-attempt id. The server dedupes on it, so a retry can never double-count. */
export function newAttemptId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

export type SubmitFn = (input: any) => Promise<any>;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A 4xx is a definitive answer from the server; anything else may be transport. */
function isRetryable(err: any): boolean {
  const status = err?.status ?? err?.statusCode;
  if (typeof status === 'number') return status >= 500 || status === 408 || status === 429;
  // authedFetch throws Error("Something went wrong (404)") for HTTP failures, so
  // pull the code back out rather than treating every Error as transport.
  const m = /\((\d{3})\)/.exec(String(err?.message ?? ''));
  if (m) {
    const code = Number(m[1]);
    return code >= 500 || code === 408 || code === 429;
  }
  return true; // no status at all: offline, DNS, aborted, CORS. Worth retrying.
}

function read(): QueuedAttempt[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function write(list: QueuedAttempt[]) {
  try {
    // Keep the newest if this ever grows: an unbounded queue in localStorage
    // would eventually throw QuotaExceeded on every write.
    localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX_QUEUED)));
  } catch {
    /* storage full or disabled: nothing useful to do here */
  }
}

export function enqueue(a: Omit<QueuedAttempt, 'queuedAt'>) {
  write([...read(), { ...a, queuedAt: Date.now() }]);
}

function dequeue(clientAttemptId: string) {
  write(read().filter((a) => a.clientAttemptId !== clientAttemptId));
}

export function pendingCount(): number {
  return read().length;
}

/**
 * Submit an attempt, retrying transient failures. Resolves with the server
 * result. Rejects only after the attempt has been safely queued for later.
 *
 * WRITE-AHEAD: the attempt is persisted BEFORE the first request and removed
 * only once the server confirms it. Queueing after the retries exhausted left a
 * ~2s window in which navigating away, backgrounding the app or an iOS WebView
 * suspend orphaned the promise and lost the attempt entirely — the exact failure
 * this module exists to prevent.
 */
export async function recordAttempt(submit: SubmitFn, input: Omit<QueuedAttempt, 'queuedAt'>): Promise<any> {
  enqueue(input);
  let lastErr: any;
  for (let i = 0; i <= RETRY_DELAYS_MS.length; i++) {
    try {
      const r = await submit(input);
      dequeue(input.clientAttemptId);
      return r;
    } catch (err) {
      lastErr = err;
      if (!isRetryable(err)) break;
      const delay = RETRY_DELAYS_MS[i];
      if (delay !== undefined) await sleep(delay);
    }
  }
  // Permanent rejection: drop it, replaying would never succeed. Transient
  // failures stay queued for flushQueue to pick up.
  if (!isRetryable(lastErr)) dequeue(input.clientAttemptId);
  throw lastErr;
}

/**
 * Drain the queue. Safe to call often; it exits immediately when empty and
 * never runs two drains at once.
 */
let draining = false;
export async function flushQueue(submit: SubmitFn): Promise<number> {
  if (draining) return 0;
  const queued = read();
  if (queued.length === 0) return 0;

  draining = true;
  const remaining: QueuedAttempt[] = [];
  let sent = 0;
  try {
    for (const a of queued) {
      try {
        const { queuedAt, ...input } = a;
        await submit(input);
        sent++;
      } catch (err) {
        // Still unreachable: keep it for next time. A permanent rejection is
        // dropped, since replaying it forever would never succeed.
        if (isRetryable(err)) remaining.push(a);
      }
    }
  } finally {
    write(remaining);
    draining = false;
  }
  return sent;
}
